import { apiService } from '@/shared/services/apiServices';
import serviceRequestEndpoints from './serviceRequestEndpoints';

/**
 * Fully-described media resource sent in the `attachments` array on the
 * service-request status PATCH. Shape matches the backend `MediaResource`
 * contract: a file already uploaded to S3 via a presigned URL.
 */
export interface ServiceRequestAttachmentMedia {
  isUploaded: true;
  /** S3 object key returned by the presigned-URL flow (no leading slash). */
  path: string;
  /** Media resource UUID. */
  resourceId: string;
  filename: string;
  fileType: string;
  fileSize: number;
}

/** Single entry returned by the batch presigned-URL endpoint. */
interface PresignedUrlFile {
  originalPath: string;
  /** Final resolved S3 key. */
  path: string;
  /** Presigned PUT URL (signed with `x-amz-tagging: status=provisional`). */
  url: string;
  /** Media resource UUID generated by the backend. */
  resourceId?: string;
}

export interface ServiceRequestPresignedUrlsResult {
  success: boolean;
  data?: { files: PresignedUrlFile[] };
  error?: string;
}

/**
 * Generates provisional presigned S3 upload URLs for service-request
 * completion attachments.
 *
 * URLs are signed with `x-amz-tagging: status=provisional` so abandoned uploads
 * (tab close, navigate away before submit) are auto-expired by the S3 lifecycle
 * rule. On a successful status PATCH the backend re-tags them as confirmed.
 *
 * @param paths - Desired S3 key hints, one per file. Use the MediaKey format
 *   `service-requests/{id}/attachments/{uuid}/{filename}`.
 */
export const generateServiceRequestAttachmentPresignedUrls = async (
  paths: string[]
): Promise<ServiceRequestPresignedUrlsResult> => {
  try {
    const result = await apiService.postAPI(
      serviceRequestEndpoints.PRESIGNED_URLS_BATCH,
      { paths, provisional: true }
    );
    if (result.error || result.isNetworkError) {
      return { success: false };
    }
    return {
      success: true,
      data: result.data as { files: PresignedUrlFile[] },
    };
  } catch {
    return { success: false };
  }
};

/**
 * Deletes a provisional S3 attachment the user removed before submitting.
 *
 * Fire-and-forget safe — failures are swallowed because the S3 lifecycle rule
 * expires abandoned provisional objects anyway.
 *
 * @param s3Key - The S3 object key (e.g. `service-requests/…/attachments/…`).
 */
export const deleteProvisionalServiceRequestAttachment = async (
  s3Key: string
): Promise<void> => {
  try {
    await apiService.deleteAPI(serviceRequestEndpoints.MEDIA_RESOURCES, {
      path: s3Key,
    });
  } catch {
    // Intentionally ignored — lifecycle rule cleans up provisional objects.
  }
};
